import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ThemeContext from '../../contexts/ThemeContext';
import PricingContext from '../../contexts/PricingContext';
import {
    estimateCustomPlanMonthly,
    formatPlanMoney,
    hasAllCountSelections,
    hasBillableSelection,
    hasMinimumForEstimate,
    isReadyToSubmit,
} from '../../utils/customPlanPricing';
import './CustomPlanBuilder.css';

const CustomPlanBuilder = ({ config, wspLink, dir, txtAlign, font = 'he', cur = '₪' }) => {
    const { theme } = useContext(ThemeContext);
    const { pricingPlan } = useContext(PricingContext);
    const location = useLocation();
    const segment = location.pathname.split('/').filter(Boolean)[0];
    const fontClass = segment === 'en' ? 'he' : font;
    const isYearly = pricingPlan === 'yearly';

    const [open, setOpen] = useState(false);
    const [posts, setPosts] = useState('');
    const [reels, setReels] = useState('');
    const [stories, setStories] = useState('');
    const [photoshoot, setPhotoshoot] = useState('');
    const [ads, setAds] = useState('');
    const [adsDailyBudget, setAdsDailyBudget] = useState('');
    const [website, setWebsite] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [notes, setNotes] = useState('');

    const wantsAds = ads === 'yes';

    const selection = useMemo(
        () => ({
            posts,
            reels,
            stories,
            photoshoot,
            ads,
            adsDailyBudget,
            website,
        }),
        [posts, reels, stories, photoshoot, ads, adsDailyBudget, website]
    );

    const countsChosen = hasAllCountSelections(selection);
    const hasBillable = hasBillableSelection(selection);
    const canEstimate = hasMinimumForEstimate(selection);
    const canSubmit = isReadyToSubmit(selection);

    const priceEstimate = useMemo(() => {
        if (!canEstimate) return null;
        return estimateCustomPlanMonthly(selection);
    }, [canEstimate, selection]);

    const estimateDisplay = useMemo(() => {
        if (!priceEstimate) return null;
        const { min, max } = priceEstimate;
        if (min === max) {
            return formatPlanMoney(min, cur, isYearly);
        }
        return `${formatPlanMoney(min, cur, isYearly)} – ${formatPlanMoney(max, cur, isYearly)}`;
    }, [priceEstimate, cur, isYearly]);

    const estimateHintText =
        canEstimate && estimateDisplay
            ? config.estimateHint
            : countsChosen && !hasBillable
              ? config.estimateEmpty
              : wantsAds && !adsDailyBudget
                ? config.adsBudgetHint || config.estimateIncomplete
                : config.estimateIncomplete;

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === 'Escape' && setOpen(false);
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    if (!config) return null;

    const optionLabel = (options, value) =>
        options?.find((o) => o.value === value)?.label || '—';

    const budgetChipLabel = (opt) => `${cur}${opt}`;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        const lines = [
            config.wspLead,
            '',
            `• ${config.posts}: ${posts}`,
            `• ${config.reels}: ${reels}`,
            `• ${config.stories}: ${stories}`,
            `• ${config.photoshoots}: ${optionLabel(config.photoshootOptions, photoshoot)}`,
            `• ${config.ads}: ${optionLabel(config.adsOptions, ads)}`,
        ];

        if (wantsAds && adsDailyBudget) {
            lines.push(
                `  ${config.adsDailyBudget}: ${budgetChipLabel(adsDailyBudget)}`
            );
        }

        lines.push(
            `• ${config.website}: ${optionLabel(config.websiteOptions, website)}`
        );

        if (priceEstimate && estimateDisplay) {
            const period = isYearly ? config.estimatePerYear : config.estimatePerMonth;
            lines.push(
                '',
                `• ${config.estimateInMessage}: ${estimateDisplay} ${period}`
            );
        }

        if (name) lines.push(`• ${config.name}: ${name}`);
        if (phone) lines.push(`• ${config.phone}: ${phone}`);
        if (notes) lines.push(`• ${config.notes}: ${notes}`);

        const url = `${wspLink}?text=${encodeURIComponent(lines.join('\n'))}`;
        window.open(url, '_blank');
        setOpen(false);
    };

    const countChipGrid = (id, label, options, value, onChange, formatLabel) => (
        <div className="CustomPlan-field CustomPlan-chip-field" key={id}>
            <span className="CustomPlan-chip-label" id={`${id}-label`}>
                {label}
            </span>
            <div
                className="CustomPlan-chip-grid"
                role="radiogroup"
                aria-labelledby={`${id}-label`}
            >
                {options.map((opt) => (
                    <button
                        key={opt}
                        type="button"
                        className={`CustomPlan-chip ${
                            value === opt ? 'is-selected' : ''
                        }`}
                        style={{ color: theme.text }}
                        onClick={() => onChange(opt)}
                        aria-pressed={value === opt}
                    >
                        {formatLabel ? formatLabel(opt) : opt}
                    </button>
                ))}
            </div>
        </div>
    );

    const choiceChipGrid = (id, label, options, value, onChange) => (
        <div className="CustomPlan-field CustomPlan-chip-field" key={id}>
            <span className="CustomPlan-chip-label" id={`${id}-label`}>
                {label}
            </span>
            <div
                className="CustomPlan-chip-grid CustomPlan-chip-grid--choices"
                role="radiogroup"
                aria-labelledby={`${id}-label`}
            >
                {options.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        className={`CustomPlan-chip CustomPlan-chip--text ${
                            value === opt.value ? 'is-selected' : ''
                        }`}
                        style={{ color: theme.text }}
                        onClick={() => onChange(opt.value)}
                        aria-pressed={value === opt.value}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    );

    const periodSuffix = isYearly ? config.estimatePerYear : config.estimatePerMonth;

    return (
        <div className={`CustomPlan-wrap ${fontClass}`}>
            <button
                type="button"
                className="CustomPlan-cta btnGradient"
                onClick={() => setOpen(true)}
            >
                {config.cta}
            </button>

            {open && (
                <div
                    className="CustomPlan-overlay"
                    role="presentation"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className={`CustomPlan-modal CustomPlan-modal--${theme.mode} ${fontClass}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="custom-plan-title"
                        style={{
                            backgroundColor: theme.cardsBg,
                            color: theme.text,
                            direction: dir,
                            textAlign: txtAlign,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="CustomPlan-close"
                            onClick={() => setOpen(false)}
                            aria-label={config.close}
                            style={{ color: theme.text }}
                        >
                            ×
                        </button>

                        <h3 id="custom-plan-title" className="CustomPlan-title">
                            {config.title}
                        </h3>
                        <p className="CustomPlan-subtitle">{config.subtitle}</p>

                        <div
                            className={`CustomPlan-estimate ${
                                canEstimate ? 'CustomPlan-estimate--ready' : ''
                            }`}
                            aria-live="polite"
                        >
                            <span className="CustomPlan-estimate-label">
                                {config.estimateLabel}
                            </span>
                            {canEstimate && estimateDisplay ? (
                                <>
                                    <span className="CustomPlan-estimate-value">
                                        {estimateDisplay}
                                        <span className="CustomPlan-estimate-period">
                                            {periodSuffix}
                                        </span>
                                    </span>
                                    <span className="CustomPlan-estimate-hint">
                                        {config.estimateHint}
                                    </span>
                                </>
                            ) : (
                                <span className="CustomPlan-estimate-hint">
                                    {estimateHintText}
                                </span>
                            )}
                        </div>

                        <form className="CustomPlan-form" onSubmit={handleSubmit}>
                            {countChipGrid(
                                'cpp-posts',
                                config.posts,
                                config.postsCountOptions,
                                posts,
                                setPosts
                            )}
                            {countChipGrid(
                                'cpp-stories',
                                config.stories,
                                config.storiesCountOptions,
                                stories,
                                setStories
                            )}
                            {countChipGrid(
                                'cpp-reels',
                                config.reels,
                                config.reelsCountOptions,
                                reels,
                                setReels
                            )}

                            {config.photoshootOptions?.length > 0 &&
                                choiceChipGrid(
                                    'cpp-photoshoot',
                                    config.photoshoots,
                                    config.photoshootOptions,
                                    photoshoot,
                                    setPhotoshoot
                                )}

                            {config.adsOptions?.length > 0 &&
                                choiceChipGrid(
                                    'cpp-ads',
                                    config.ads,
                                    config.adsOptions,
                                    ads,
                                    (v) => {
                                        setAds(v);
                                        if (v !== 'yes') setAdsDailyBudget('');
                                    }
                                )}

                            {wantsAds &&
                                config.adsBudgetOptions?.length > 0 &&
                                countChipGrid(
                                    'cpp-ads-budget',
                                    config.adsDailyBudget,
                                    config.adsBudgetOptions,
                                    adsDailyBudget,
                                    setAdsDailyBudget,
                                    budgetChipLabel
                                )}

                            {config.websiteOptions?.length > 0 &&
                                choiceChipGrid(
                                    'cpp-website',
                                    config.website,
                                    config.websiteOptions,
                                    website,
                                    setWebsite
                                )}

                            <div className="CustomPlan-field">
                                <label htmlFor="cpp-name">{config.name}</label>
                                <input
                                    id="cpp-name"
                                    className="CustomPlan-input"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>

                            <div className="CustomPlan-field">
                                <label htmlFor="cpp-phone">{config.phone}</label>
                                <input
                                    id="cpp-phone"
                                    className="CustomPlan-input"
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                />
                            </div>

                            <div className="CustomPlan-field">
                                <label htmlFor="cpp-notes">{config.notes}</label>
                                <textarea
                                    id="cpp-notes"
                                    className="CustomPlan-textarea"
                                    rows={3}
                                    placeholder={config.notesPlaceholder}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>

                            <button
                                type="submit"
                                className="CustomPlan-submit btnGradient"
                                disabled={!canSubmit}
                            >
                                {config.submit}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomPlanBuilder;
